/**
 * §industry-aware-dash B2 (2026-08-08) — one shell, five skins, zero forks.
 *
 * The tenant's resolved industry PACK (served by /api/admin/tenant-status as
 * `vertical` — never the tenant id string) selects a typed DashboardConfig:
 * vocabulary labels, the transactions surface's name, whether revenue framing
 * renders, and which nav paths hide. Every component underneath is shared and
 * parameterized; this file is the only place a per-industry difference lives.
 *
 * Two rules, learned the hard way:
 *
 *  1. KEY ON THE PACK, NEVER THE TENANT ID. The tenant-id compares this
 *     replaces were single-industry code wearing multi-tenant clothes —
 *     every new hospitality tenant would have missed the hospitality
 *     experience, and every rename would have broken it. §hotel-removal
 *     (2026-08-19) retired the last of them along with the demo tenant's
 *     own surfaces: a hospitality tenant now gets this config's skin over
 *     the shared pages, which is the only hospitality path there is.
 *
 *  2. NO PACK MEANS THE NEUTRAL DEFAULT, never another trade's. Mirrors
 *     industry_packs / action_policy: an unclassified tenant behaves exactly
 *     like the platform behaved before this file existed.
 *
 * The labels are the site mockups' own vocabulary (orikin.ai industry pages,
 * read in full 2026-08-08): the five dashboards share ONE anatomy with
 * different nouns — Orders / Bookings / Appointments / Viewings /
 * Applications; Customers / Guests / Patients / Clients; "Escalated to you" /
 * "Sent to a clinician" / "Sent to an adviser" / "Sent to an agent".
 */

export type PackKey =
  | "retail"
  | "clinic"
  | "hospitality"
  | "real_estate"
  | "financial_services";

export interface DashboardConfig {
  /** Pack key, or "default" for the neutral config. */
  key: PackKey | "default";
  /** The site's industry name — header captions, workspace cards. */
  industryLabel: string;
  /** What this trade calls the transactions surface (nav + page title). */
  ordersLabel: string;
  /** Singular form for counters and captions. */
  orderNoun: string;
  /** What this trade calls the people it serves. */
  customersLabel: string;
  /** The fourth KPI — where the agent needed a person. */
  escalationLabel: string;
  /**
   * Healthcare's dashboard carries NO revenue framing anywhere — the site's
   * clinic mockup has no revenue tile, pulse, or money language. Everyone
   * else keeps it.
   */
  showRevenue: boolean;
  /** Nav paths this trade hides entirely (RBAC still applies on top). */
  hiddenNavPaths: readonly string[];
}

const DEFAULT_CONFIG: DashboardConfig = {
  key: "default",
  industryLabel: "Business",
  ordersLabel: "Orders",
  orderNoun: "order",
  customersLabel: "Customers",
  escalationLabel: "Escalated to you",
  showRevenue: true,
  hiddenNavPaths: [],
};

const CONFIGS: Record<PackKey, DashboardConfig> = {
  retail: {
    ...DEFAULT_CONFIG,
    key: "retail",
    industryLabel: "Retail & E-Commerce",
  },
  hospitality: {
    ...DEFAULT_CONFIG,
    key: "hospitality",
    industryLabel: "Travel & Hospitality",
    ordersLabel: "Bookings",
    orderNoun: "booking",
    customersLabel: "Guests",
  },
  clinic: {
    ...DEFAULT_CONFIG,
    key: "clinic",
    industryLabel: "Healthcare",
    ordersLabel: "Appointments",
    orderNoun: "appointment",
    customersLabel: "Patients",
    escalationLabel: "Sent to a clinician",
    // "No revenue framing anywhere" — the clinic mockup's scoreboard has no
    // money on it, and a patient dashboard that opens on revenue reads as a
    // shop. Products stays visible (a clinic's services list lives there).
    showRevenue: false,
  },
  real_estate: {
    ...DEFAULT_CONFIG,
    key: "real_estate",
    industryLabel: "Real Estate & Property",
    ordersLabel: "Viewings",
    orderNoun: "viewing",
    customersLabel: "Clients",
    escalationLabel: "Sent to an agent",
  },
  financial_services: {
    ...DEFAULT_CONFIG,
    key: "financial_services",
    industryLabel: "Financial Services",
    ordersLabel: "Applications",
    orderNoun: "application",
    customersLabel: "Clients",
    escalationLabel: "Sent to an adviser",
    // Money is the trade; revenue framing stays, but products-as-goods
    // doesn't fit — the Products page holds the product/rate sheet, which
    // is exactly what the financial pack needs visible. Keep everything.
  },
};

export function dashboardConfigFor(
  vertical: string | null | undefined,
): DashboardConfig {
  const key = (vertical || "").trim().toLowerCase() as PackKey;
  return CONFIGS[key] ?? DEFAULT_CONFIG;
}

import { useTenantStatus } from "./tenant";

/**
 * The hook every shared surface uses. Resolves through tenant-status (5-min
 * cached); while loading — or on an older backend that doesn't serve
 * `vertical` — returns the neutral default, i.e. exactly the pre-B2 UI.
 */
export function useDashboardConfig(): DashboardConfig {
  const { data } = useTenantStatus();
  return dashboardConfigFor(data?.vertical);
}
